import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { confirmDialog } from "@/components/ui";
import { PageActionProps, PageTitle } from "@/components/PageTitle";
import { PageWrapper } from "@/components/PageWrapper";
import { Blog, BlogServices, ContentPageTheme, ContentPageThemeServices } from "@/services/bedrock";
import { useBlogSearch } from "@/utils/useBlogSearch";
import { blogDetailUrl, blogListUrl, URLS, useInterval, usePlayerSettings, useSocialCoachSession } from "@/utils";
import { CoachSettings } from "@/interfaces";
import { toast } from "sonner";
import { BlogProcessingOverlay } from "./BlogModal/BlogProcessingOverlay";
import { BlogEditor, EditableBlog, nextEditorDraft, toEditableBlog } from "./BlogModal/BlogEditor";
import { BlogStatusBadge } from "./BlogStatusBadge";
import { BlogPreview, BlogPreviewHandle } from "./BlogPreview";
import { themeForPreview } from "./blogPreviewPayload";

export const BlogPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const contentPageId = Number(new URLSearchParams(location.search).get("contentPageId") || "") || undefined;

  const [blog, setBlog] = useState<Blog>();
  const [draft, setDraft] = useState<EditableBlog>();
  const [loading, setLoading] = useState(false);
  const [themes, setThemes] = useState<ContentPageTheme[]>([]);

  const { isPlayer } = useSocialCoachSession();
  const { hasSetting } = usePlayerSettings(blog?.playerId ?? undefined);
  // Blogs reuse Video Page themes, so they follow the same Player-level permission.
  const canChangeTheme = !isPlayer || !!hasSetting(CoachSettings.PLAYER_MVP_THEMES);
  const listUrl = blogListUrl(!!isPlayer);

  const {
    createBlogMutation,
    updateBlogMutation,
    publishBlogMutation,
    unpublishBlogMutation,
    regenerateBlogMutation,
    archiveBlogMutation,
    invalidateBlogs,
  } = useBlogSearch();

  const seededFromRef = useRef<Blog>();
  const creatingRef = useRef<Promise<Blog>>();
  const previewRef = useRef<BlogPreviewHandle>(null);

  const applyBlog = useCallback((next: Blog, options?: { reseedEditor?: boolean }) => {
    const seededFrom = seededFromRef.current;
    seededFromRef.current = next;

    setBlog(next);
    setDraft(current => nextEditorDraft(next, current, seededFrom, options?.reseedEditor));
  }, []);

  const loadBlog = useCallback(
    async (blogId: number) => {
      try {
        applyBlog(await BlogServices.getBlog(blogId));
      } catch (e) {
        console.error(e);
        toast.error("Failed to load the blog");
      }
    },
    [applyBlog]
  );

  useEffect(() => {
    let cancelled = false;

    const start = async () => {
      setLoading(true);
      try {
        if (id === "new") {
          if (!contentPageId) {
            toast.error("Choose a Video Page to write a blog from.");
            history.replace(listUrl);
            return;
          }
          creatingRef.current ??= createBlogMutation.mutateAsync(contentPageId);
          const created = await creatingRef.current;
          if (cancelled) return;
          applyBlog(created);
          history.replace(blogDetailUrl(!!isPlayer, created.id));
          return;
        }

        if (!isFinite(Number(id))) {
          history.replace(listUrl);
          return;
        }

        await loadBlog(Number(id));
      } catch (e) {
        console.error(e);
        toast.error("Failed to open the blog");
        if (!cancelled) history.replace(listUrl);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    start();
    return () => {
      cancelled = true;
    };
  }, [id, contentPageId]);

  useEffect(() => {
    ContentPageThemeServices.getPageThemes({ perPage: 100 })
      .then(response => setThemes(response.content))
      .catch(console.error);
  }, []);

  const selectedThemeId = draft?.themeId ?? blog?.contentPageThemeId;
  useEffect(() => {
    if (!selectedThemeId || themes.some(theme => theme.id === selectedThemeId)) return;

    ContentPageThemeServices.getPageTheme(selectedThemeId)
      .then(theme => setThemes(current => (current.some(item => item.id === theme.id) ? current : [theme, ...current])))
      .catch(console.error);
  }, [selectedThemeId, themes]);

  useInterval(
    async () => {
      if (blog) await loadBlog(blog.id);
    },
    blog?.status === "GENERATING" ? 2000 : undefined
  );

  const isDirty = useMemo(() => {
    if (!blog || !draft) return false;
    return JSON.stringify(draft) !== JSON.stringify(toEditableBlog(blog));
  }, [blog, draft]);

  const save = async () => {
    if (!blog || !draft) return;
    const updated = await updateBlogMutation.mutateAsync({
      blogId: blog.id,
      request: {
        title: draft.title,
        subtitle: draft.subtitle,
        metaDescription: draft.metaDescription,
        excerpt: draft.excerpt,
        tags: draft.tags.map(tag => tag.trim()).filter(Boolean),
        themeId: draft.themeId,
        body: draft.body,
      },
    });
    applyBlog(updated, { reseedEditor: true });
  };

  const publish = async () => {
    if (!blog) return;
    if (isDirty) await save();
    applyBlog(await publishBlogMutation.mutateAsync(blog.id));
  };

  const unpublish = async () => {
    if (!blog) return;
    applyBlog(await unpublishBlogMutation.mutateAsync(blog.id));
  };

  const regenerate = async () => {
    if (!blog) return;
    const confirmed = await confirmDialog({
      title: "Rewrite this blog?",
      description: "The current article will be replaced with a newly written one.",
      confirmLabel: "Rewrite",
    });
    if (!confirmed) return;
    setDraft(undefined);
    applyBlog(await regenerateBlogMutation.mutateAsync(blog.id));
  };

  const archive = async () => {
    if (!blog) return;
    const confirmed = await confirmDialog({
      title: "Delete this blog?",
      description: "This blog and its public page will no longer be available.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!confirmed) return;
    await archiveBlogMutation.mutateAsync(blog.id);
    invalidateBlogs();
    history.push(listUrl);
  };

  const shareUrl = blog?.trackedUrl || blog?.url || "";

  const copyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard!");
    } catch (e) {
      console.error(e);
      toast.error("Could not copy the link");
    }
  };

  const showOverlay = loading || !blog || !draft || blog.status === "GENERATING" || blog.status === "FAILED";
  const permitted = blog?.permittedEvents ?? [];
  const readOnly = blog?.status === "GENERATING";
  const themeOptions = useMemo(() => themes.map(theme => ({ id: theme.id, label: theme.name })), [themes]);
  const previewTheme = useMemo(
    () =>
      draft ? themeForPreview(draft, themes, blog?.effectiveContentPageTheme ?? blog?.contentPageTheme) : undefined,
    [draft, themes, blog?.effectiveContentPageTheme, blog?.contentPageTheme]
  );
  const complianceWarning = blog?.processingSteps?.find(step => step.status === "COMPLETE_WITH_WARNINGS");

  const moreActions: PageActionProps[] = [];
  if (blog && blog.status !== "PUBLISHED") {
    moreActions.push({ text: "Open page", onClick: () => window.open(blog.url, "_blank") });
  }
  if (permitted.includes("regenerate")) {
    moreActions.push({ text: "Rewrite with AI", onClick: regenerate, disabled: regenerateBlogMutation.isPending });
  }
  if (permitted.includes("unpublish")) {
    moreActions.push({ text: "Unpublish", onClick: unpublish, disabled: unpublishBlogMutation.isPending });
  }
  moreActions.push({ text: "Delete", onClick: archive });

  const secondaryAction: PageActionProps = {
    text: isDirty ? "Save" : "Saved",
    onClick: save,
    disabled: !isDirty || updateBlogMutation.isPending,
    loading: updateBlogMutation.isPending,
  };

  const primaryAction: PageActionProps | undefined = permitted.includes("publish")
    ? {
        text: "Publish",
        onClick: publish,
        disabled: publishBlogMutation.isPending,
        loading: publishBlogMutation.isPending,
      }
    : blog?.status === "PUBLISHED"
    ? { text: "View live", onClick: () => window.open(blog.url, "_blank") }
    : undefined;

  return (
    <div className="flex flex-col gap-5">
      <PageTitle
        title={blog?.title || (id === "new" ? "New blog" : "Blog")}
        hideSeparator
        breadcrumbs={[
          { name: "Library", link: listUrl },
          { name: "Blogs", link: listUrl },
        ]}
        primaryAction={showOverlay ? undefined : primaryAction}
        secondaryAction={showOverlay ? undefined : secondaryAction}
        actions={showOverlay ? undefined : moreActions}
      />

      {showOverlay ? (
        <div className="relative min-h-100">
          <BlogProcessingOverlay blog={blog} onRetry={regenerate} retrying={regenerateBlogMutation.isPending} />
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <BlogStatusBadge status={blog!.status} />
            {isDirty && (
              <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-800">
                Unsaved
              </span>
            )}
            {!!blog!.readingTimeMinutes && (
              <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                {blog!.readingTimeMinutes} min read
              </span>
            )}
            {typeof blog!.totalLinkClicks === "number" && blog!.status === "PUBLISHED" && (
              <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                {blog!.totalLinkClicks} clicks
              </span>
            )}
            {complianceWarning && (
              <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-[11px] font-semibold text-amber-800">
                {complianceWarning.details}
              </span>
            )}
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-grow min-w-0">
              <BlogEditor
                draft={draft!}
                readOnly={readOnly}
                themeOptions={themeOptions}
                canChangeTheme={canChangeTheme}
                themeSettingsUrl={isPlayer ? URLS.player.mvpSettings : URLS.coach.mvpSettings}
                onChange={patch => setDraft({ ...draft!, ...patch })}
                onSectionHover={index => previewRef.current?.highlightSection(index)}
                onSectionExpand={(index, blockIndex) => previewRef.current?.scrollToSection(index, blockIndex)}
                onSectionInputBlur={() => previewRef.current?.clearFocusedBlock()}
                onSectionDrag={index => {
                  if (index == null) {
                    previewRef.current?.unfollowSection();
                    return;
                  }
                  previewRef.current?.followSection(index);
                }}
                onPartFocus={part => {
                  if (part == null) {
                    previewRef.current?.highlightPart(null);
                    return;
                  }
                  previewRef.current?.scrollToPart(part);
                }}
              />
            </div>

            <div className="lg:w-[42%] min-w-0 self-start lg:sticky lg:top-4 lg:z-10">
              <BlogPreview
                ref={previewRef}
                blog={blog!}
                draft={draft!}
                theme={previewTheme}
                author={blog!.author ?? undefined}
                shareUrl={blog!.status === "PUBLISHED" ? shareUrl : undefined}
                onCopyShareUrl={blog!.status === "PUBLISHED" ? copyShareUrl : undefined}
                onOpenPage={() => window.open(blog!.url, "_blank")}
                className="h-[min(70dvh,640px)] min-h-80 lg:h-[max(20rem,calc(100dvh-12rem))]"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export const PlayerBlogPage: React.FC = () => (
  <PageWrapper hideSeparator className="max-w-325">
    <BlogPage />
  </PageWrapper>
);
